import { axiosInstance } from 'config';
import { AsyncAxiosRequestConfig } from 'core/common';
import {
  AddressResponse,
  GetClientByAddressParams,
  BusinessType,
} from 'core/domicile';
import { PhoneCompanies, PhoneTypes } from 'core/phones';
import { capitalize, upperCaseObjectValues } from 'utils/helpers';
import { SearchClientParams } from 'store/modules/main/main-slice';
import {
  ClientAdditionalContact,
  ClientAddressResponse,
  ClientResponse,
} from './client.model';
import { isRuc } from 'core/document-types';
import { GetSdsToPdfResponse } from 'core/sds/sds.model';

export interface SearchSdsOtByNumberParams {
  form_number?: string;
  ot_number?: string;
  username: string;
  flag_ruc?: boolean;
  isDth?: boolean;
}

class ClientsAPI {
  getValidateSeller({
    params,
  }: AsyncAxiosRequestConfig<{
    entityId: string;
    documentNumber: string;
    documentType: string;
    business: string;
  }>) {
    const { entityId, documentNumber, documentType, business } = params;
    return axiosInstance
      .get<any>(
        `/servicios_fijos/v1/validate_seller?entity_id=${entityId}&document_number=${documentNumber}&document_type=${documentType}&business=${business}`
      )
      .then(({ data }) => data);
  }

  getClientByDocument({
    params,
    cancelToken,
  }: AsyncAxiosRequestConfig<SearchClientParams>) {
    const {
      documentType,
      documentNumber,
      validatorDigit,
      entityId,
      isEmployee,
      business_type = BusinessType.FIJO,
    } = params;

    const getClientAddresses = (clientId: number) =>
      axiosInstance.get<AddressResponse>(
        `/servicios_fijos/v1/delivery_domiciles?client_id=${clientId}`,
        {
          cancelToken,
          params: {
            flag_ruc: isRuc(documentType),
            business_type,
          },
        }
      );

    return axiosInstance
      .get<ClientResponse>('/servicios_fijos/v1/clients', {
        params: {
          document_type: documentType,
          document_number: isRuc(documentType)
            ? `${documentNumber}-${validatorDigit}`
            : documentNumber,
          entity_id: entityId,
          is_employee: isEmployee,
        },
        cancelToken,
      })
      .then(({ data }) =>
        getClientAddresses(data.clientId).then((response) => {
          const {
            data: { deliveryDomiciles: addresses, domicileQuantity },
          } = response;

          return {
            data,
            addresses: upperCaseObjectValues(addresses),
            domicileQuantity,
          };
        })
      )
      .then(({ data, addresses, domicileQuantity }) => {
        let sitio;
        let additional: ClientAdditionalContact[] = [];

        data.contacts.forEach((contact, index, array) => {
          const phoneTypeNormalize = capitalize(contact.phoneType)?.trim();
          const phoneType = PhoneTypes[phoneTypeNormalize] || '';

          const capPhoneCompany = capitalize(contact.phoneCompany)?.trim();
          const phoneCompany = PhoneCompanies[capPhoneCompany] || '';

          if (contact.type.toUpperCase() === 'SITIO') {
            sitio = {
              ...contact,
              email: contact.email.replace(/\s/g, ''),
              name: contact.name ?? '',
              surname: contact.surname ?? '',
              areaCode: contact.areaCode?.trim() || '',
              phoneType,
              phoneCompany,
            };
          }

          if (contact.type.toUpperCase() === 'ALTERNATIVO') {
            additional.push({
              ...contact,
              email: contact.email.replace(/\s/g, ''),
              name: contact.name ?? '',
              surname: contact.surname ?? '',
              areaCode: contact.areaCode?.trim() || '',
              phoneType,
              phoneCompany,
            });
          }
        });

        return {
          ...data,
          company: data.companySurname,
          birthDate: data.birthDate?.substring(
            0,
            data.birthDate.lastIndexOf('+')
          ),
          addresses,
          domicileQuantity,
          contacts: {
            sitio,
            additional: additional.slice(0, 2),
          },
        };
      });
  }

  getClientSdsOt({
    params,
  }: AsyncAxiosRequestConfig<SearchSdsOtByNumberParams>) {
    return axiosInstance
      .get<GetSdsToPdfResponse>(`/servicios_fijos/v1/bills/search`, {
        params,
      })
      .then(({ data }) => data);
  }

  getClientByAddress({
    params,
  }: AsyncAxiosRequestConfig<GetClientByAddressParams>) {
    const {
      accountNumberId,
      addressCity,
      regionName,
      domicile_id,
      addressNumber,
      addressStreet,
      addressFloor = '',
      addressFlat = '',
      addressTower,
      businessType,
      suburb,
    } = params;

    return axiosInstance
      .get<ClientAddressResponse>(
        `/servicios_fijos/v1/delivery_domiciles/${addressNumber}?region_name=${regionName?.name}&address_city=${addressCity?.name}&address_suburb=${suburb?.name}&address_floor=${addressFloor}&address_flat=${addressFlat}&tower=${addressTower}&business=${businessType}&address_street=${addressStreet}`,
        {
          params: {
            domicile_id: domicile_id,
            account_id: accountNumberId,
          },
        }
      )
      .then(({ data }) => data);
  }
}

export default new ClientsAPI();
